import { useState, useEffect } from "react";
import "../../../css/RefundModal.css";
import { toExternalUrl } from '../../../utils/url';

function formatWithComma(value) {
  const digits = String(value).replace(/[^0-9]/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString();
}

function parseComma(value) {
  return Number(String(value).replace(/,/g, "")) || 0;
}

/**
 * API 응답에서 환불된 adReviewDeleteId 목록을 추출.
 * 여러 응답 형태를 허용하고, 빈 경우 submitted 기준으로 폴백.
 */
function extractResultItems(apiResponse, submitted, originalItems, keyField) {
  let ids = null;

  if (apiResponse) {
    if (Array.isArray(apiResponse.items)) {
      ids = apiResponse.items
        .map((i) => i.adReviewDeleteId)
        .filter(Boolean);
    } else if (Array.isArray(apiResponse.refundedIds)) {
      ids = apiResponse.refundedIds;
    } else if (Array.isArray(apiResponse)) {
      ids = apiResponse
        .map((i) => (typeof i === "object" ? i.adReviewDeleteId : i))
        .filter(Boolean);
    }
  }

  // API 응답에 ID가 없으면 submitted 항목을 기준으로
  const targetIds = ids?.length > 0 ? ids : submitted.map((i) => i[keyField]);

  return targetIds.map((id) => {
    const orig = originalItems.find((i) => i[keyField] === id);
    const sub = submitted.find((i) => i[keyField] === id);
    return {
      adReviewDeleteId: id,
      reviewUrl: orig?.reviewUrl,
      refundedAmount: sub?.refundedAmount ?? 0,
    };
  });
}

/**
 * 환불 처리 모달
 * @param {boolean} props.isOpen
 * @param {Array} props.items - keyField로 지정된 필드가 행 키로 사용됨
 * @param {Function} props.onClose
 * @param {Function} props.onSubmit - (refundItems, reason) => Promise<responseData>
 *   성공 시 responseData를 resolve, 실패 시 throw
 * @param {string} [props.keyField='adCampaignMetaId']
 * @param {boolean} [props.showResultStep=false] - true면 성공 후 결과 스텝 표시. false(기본)면 기존처럼 onClose() 호출.
 */
function RefundModal({ isOpen, items = [], onClose, onSubmit, keyField = 'adCampaignMetaId', showResultStep = false }) {
  const [refundAmounts, setRefundAmounts] = useState({});
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);       // null: 입력 스텝, {}~: 결과 스텝
  const [lastSubmitted, setLastSubmitted] = useState([]); // 제출했던 항목 저장

  useEffect(() => {
    if (isOpen) {
      setRefundAmounts({});
      setReason("");
      setResult(null);
      setLastSubmitted([]);
    }
  }, [isOpen]);

  if (!isOpen || !items || items.length === 0) return null;

  const refundableItems = items.filter((i) => (i.remainingAmount ?? 0) > 0);
  const excludedCount = items.length - refundableItems.length;
  const showReviewUrl = refundableItems.some((i) => !!i.reviewUrl);

  // ── 전액 환불된 건만 있을 때 ──────────────────────────────
  if (refundableItems.length === 0) {
    return (
      <div className="refund-modal-overlay" onClick={onClose}>
        <div className="refund-modal" onClick={(e) => e.stopPropagation()}>
          <div className="refund-modal-header">
            <h3>환불 처리</h3>
            <button className="refund-modal-close-btn" onClick={onClose} aria-label="닫기">✕</button>
          </div>
          <div className="refund-modal-body">
            <p className="refund-all-excluded-msg">선택한 건이 모두 이미 전액 환불된 상태입니다.</p>
          </div>
          <div className="refund-modal-footer">
            <button className="refund-cancel-btn" onClick={onClose}>닫기</button>
          </div>
        </div>
      </div>
    );
  }

  const hasPointItem = refundableItems.some((i) => i.paymentType === "point");

  // ── 결과 스텝 ─────────────────────────────────────────────
  if (result !== null) {
    const resultItems = extractResultItems(result, lastSubmitted, refundableItems, keyField);

    return (
      <div className="refund-modal-overlay" onClick={onClose}>
        <div className="refund-modal has-url" onClick={(e) => e.stopPropagation()}>
          <div className="refund-modal-header">
            <h3 style={{ color: "#16a34a" }}>환불 처리 완료</h3>
            <button className="refund-modal-close-btn" onClick={onClose} aria-label="닫기">✕</button>
          </div>

          <div className="refund-modal-body">
            <p className="refund-excluded-notice" style={{ borderLeftColor: "#16a34a", background: "#f0fdf4", color: "#166534" }}>
              총 {resultItems.length}건이 처리되었습니다.
            </p>
            <table className="refund-modal-table">
              <thead>
                <tr>
                  <th>리뷰 URL</th>
                  <th>Campaign ID</th>
                  <th style={{ textAlign: "right" }}>환불 금액</th>
                </tr>
              </thead>
              <tbody>
                {resultItems.map((item) => (
                  <tr key={item.adReviewDeleteId}>
                    <td className="refund-url-cell">
                      {toExternalUrl(item.reviewUrl) ? (
                        <a href={toExternalUrl(item.reviewUrl)} target="_blank" rel="noopener noreferrer">
                          {item.reviewUrl}
                        </a>
                      ) : (
                        item.reviewUrl || "-"
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>{item.adReviewDeleteId}</td>
                    <td style={{ textAlign: "right" }}>
                      {(item.refundedAmount || 0).toLocaleString()}원
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="refund-modal-footer">
            <button className="refund-confirm-btn" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── 입력 스텝 ─────────────────────────────────────────────
  const handleAmountChange = (itemId, rawValue) => {
    const formatted = formatWithComma(rawValue);
    setRefundAmounts((prev) => ({ ...prev, [itemId]: formatted }));
  };

  const handleSubmit = async () => {
    for (const row of refundableItems) {
      const itemId = row[keyField];
      const entered = parseComma(refundAmounts[itemId] || "");
      if (entered > 0 && entered > row.remainingAmount) {
        alert(
          `'${row.userName}(${row.userId})' 환불 금액이 잔액(${row.remainingAmount.toLocaleString()}원)을 초과합니다.`
        );
        return;
      }
    }

    const refundItems = refundableItems
      .filter((row) => parseComma(refundAmounts[row[keyField]] || "") > 0)
      .map((row) => {
        const itemId = row[keyField];
        const enteredAmount = parseComma(refundAmounts[itemId] || "");
        return {
          [keyField]: itemId,
          adCampaignMetaId: row.adCampaignMetaId,
          refundedAmount: enteredAmount,
          refundType: enteredAmount >= row.remainingAmount ? "FULL" : "PARTIAL",
          paymentType: row.paymentType,
          adPaymentTransactionId: row.adPaymentTransactionId,
          userId: row.userId,
          cancelAmount: enteredAmount,
        };
      });

    if (refundItems.length === 0) {
      alert("환불할 금액을 하나 이상 입력해주세요.");
      return;
    }

    if (hasPointItem && !reason.trim()) {
      alert("환불 사유를 입력해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      const responseData = await onSubmit(refundItems, reason.trim() || "환불 처리");
      if (showResultStep) {
        setLastSubmitted(refundItems);
        setResult(responseData ?? {});
      } else {
        onClose();
      }
    } catch {
      // 에러는 부모 컴포넌트에서 alert 처리 — 모달 유지
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="refund-modal-overlay" onClick={onClose}>
      <div className={`refund-modal${showReviewUrl ? " has-url" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="refund-modal-header">
          <h3>환불 처리 ({refundableItems.length}건)</h3>
          <button className="refund-modal-close-btn" onClick={onClose} aria-label="닫기">✕</button>
        </div>

        <div className="refund-modal-body">
          {excludedCount > 0 && (
            <p className="refund-excluded-notice">
              선택 {items.length}건 중 {excludedCount}건은 이미 전액 환불되어 제외됩니다.
            </p>
          )}
          <table className="refund-modal-table">
            <thead>
              <tr>
                <th>신청자</th>
                {showReviewUrl && <th>리뷰 URL</th>}
                <th>원거래 금액</th>
                <th>기환불 금액</th>
                <th>잔액</th>
                <th>환불할 금액</th>
                <th>환불 후 잔액</th>
              </tr>
            </thead>
            <tbody>
              {refundableItems.map((row) => {
                const itemId = row[keyField];
                const enteredAmount = parseComma(refundAmounts[itemId] || "");
                const afterRefund = row.remainingAmount - enteredAmount;
                const isNegative = afterRefund < 0;
                const originalAmount = row.totalAmount ?? row.unitCost ?? 0;

                return (
                  <tr key={itemId}>
                    <td>{row.userName}({row.userId})</td>
                    {showReviewUrl && (
                      <td className="refund-url-cell">
                        {toExternalUrl(row.reviewUrl) ? (
                          <a href={toExternalUrl(row.reviewUrl)} target="_blank" rel="noopener noreferrer">
                            {row.reviewUrl}
                          </a>
                        ) : (
                          row.reviewUrl || "-"
                        )}
                      </td>
                    )}
                    <td>{originalAmount.toLocaleString()}원</td>
                    <td>{(row.refundedAmount || 0).toLocaleString()}원</td>
                    <td>{(row.remainingAmount || 0).toLocaleString()}원</td>
                    <td>
                      <input
                        type="text"
                        className="refund-amount-input"
                        value={refundAmounts[itemId] || ""}
                        onChange={(e) => handleAmountChange(itemId, e.target.value)}
                        placeholder="0"
                      />
                    </td>
                    <td className={isNegative ? "refund-remaining-negative" : ""}>
                      {afterRefund.toLocaleString()}원
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasPointItem && (
            <div style={{ marginTop: "16px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px" }}>
                환불 사유 <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="환불 사유를 입력해주세요"
                rows={3}
                disabled={submitting}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px", resize: "none", boxSizing: "border-box", fontFamily: "inherit", outline: "none", color: "#1f2937", lineHeight: 1.6, margin: 0 }}
              />
            </div>
          )}
        </div>

        <div className="refund-modal-footer">
          <button className="refund-cancel-btn" onClick={onClose} disabled={submitting}>
            취소
          </button>
          <button className="refund-confirm-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />{" "}
                처리 중...
              </>
            ) : (
              "확인"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RefundModal;
